
import { Link } from 'react-router-dom';

export default function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-gray-900 text-white py-6 sticky top-0 z-50 shadow-lg">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 cursor-pointer">
            <img
              src="https://static.readdy.ai/image/3ca3a64fdbdd1a27f61611d5a6ab11d4/0b305659fb07b7246329e00bf7bbf5b5.png"
              alt="VAS Cyber"
              className="w-12 h-12"
            />
            <span className="text-xl font-bold">VAS Cyber</span>
          </Link>
          <Link
            to="/"
            className="px-6 py-2 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold rounded-full hover:shadow-lg hover:shadow-emerald-500/50 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            Back to Home
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="inline-block bg-emerald-500 text-white text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-full mb-6">
            Legal Agreement
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Terms of Service</h1>
          <p className="text-xl text-gray-300">
            Please read these terms carefully before using our services
          </p>
          <p className="text-sm text-gray-400 mt-4">Last Updated: January 2025</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="prose prose-lg max-w-none">
            {/* Introduction */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <i className="ri-file-text-line text-emerald-500"></i>
                Agreement to Terms
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                These Terms of Service ("Terms") constitute a legally binding agreement between you and VAS Cyber ("Company," "we," "us," or "our") concerning your access to and use of our website, services, and products.
              </p>
              <p className="text-gray-700 leading-relaxed">
                By accessing or using our services, you agree to be bound by these Terms. If you disagree with any part of these Terms, you may not access our services.
              </p>
            </div>

            {/* Services */}
            <div className="mb-12 bg-gray-50 rounded-2xl p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <i className="ri-service-line text-emerald-500"></i>
                Our Services
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                VAS Cyber provides cybersecurity, risk management, IT governance, digital forensics, and advisory services. Our services include but are not limited to:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  'Cyber Risk Assessments',
                  'Security Monitoring & Detection',
                  'Penetration Testing',
                  'IT Governance & Compliance',
                  'Digital Forensics Investigations',
                  'Security Awareness Training',
                  'Executive Advisory Services',
                  'Incident Response Planning'
                ].map((service, index) => (
                  <div key={index} className="flex items-start gap-3 bg-white rounded-lg p-4 border-l-4 border-emerald-500">
                    <i className="ri-shield-check-line text-emerald-500 text-xl flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700">{service}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* User Obligations */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <i className="ri-user-line text-emerald-500"></i>
                User Obligations and Responsibilities
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                When using our services, you agree to:
              </p>
              <ul className="space-y-3">
                {[
                  'Provide accurate, current, and complete information',
                  'Maintain the security of your account credentials',
                  'Notify us immediately of any unauthorized access',
                  'Use our services only for lawful purposes',
                  'Comply with all applicable laws and regulations',
                  'Not interfere with or disrupt our services',
                  'Not attempt to gain unauthorized access to our systems',
                  'Respect intellectual property rights'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3 text-gray-700">
                    <i className="ri-checkbox-circle-fill text-emerald-500 text-xl flex-shrink-0 mt-0.5"></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Service Agreements */}
            <div className="mb-12 bg-gradient-to-br from-emerald-50 to-cyan-50 rounded-2xl p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <i className="ri-file-list-3-line text-emerald-500"></i>
                Service Agreements and Engagement
              </h2>
              <div className="space-y-4">
                <div className="bg-white rounded-lg p-6 border-l-4 border-emerald-500">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Scope of Work</h3>
                  <p className="text-gray-700">
                    Each engagement will be governed by a separate Statement of Work (SOW) or Service Agreement that defines the specific services, deliverables, timelines, and fees.
                  </p>
                </div>
                <div className="bg-white rounded-lg p-6 border-l-4 border-cyan-500">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Client Cooperation</h3>
                  <p className="text-gray-700">
                    Clients must provide timely access to systems, information, and personnel necessary for service delivery. Delays caused by lack of cooperation may affect project timelines and costs.
                  </p>
                </div>
                <div className="bg-white rounded-lg p-6 border-l-4 border-gray-500">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Confidentiality</h3>
                  <p className="text-gray-700">
                    Both parties agree to maintain strict confidentiality of all sensitive information exchanged during the engagement, subject to separate Non-Disclosure Agreements (NDAs) where applicable.
                  </p>
                </div>
              </div>
            </div>

            {/* Payment Terms */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <i className="ri-money-dollar-circle-line text-emerald-500"></i>
                Payment Terms and Fees
              </h2>
              <div className="space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  Payment terms will be specified in individual service agreements. General terms include:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { title: 'Invoicing', desc: 'Invoices issued according to agreed schedule' },
                    { title: 'Payment Due', desc: 'Payment due within 30 days of invoice date' },
                    { title: 'Late Payments', desc: 'Late fees may apply to overdue payments' },
                    { title: 'Currency', desc: 'All fees quoted in agreed currency' },
                    { title: 'Taxes', desc: 'Fees exclude applicable taxes unless stated' },
                    { title: 'Expenses', desc: 'Reasonable expenses billed separately' }
                  ].map((term, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <h3 className="text-lg font-bold text-gray-900 mb-1">{term.title}</h3>
                      <p className="text-sm text-gray-600">{term.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Intellectual Property */}
            <div className="mb-12 bg-gray-50 rounded-2xl p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <i className="ri-copyright-line text-emerald-500"></i>
                Intellectual Property Rights
              </h2>
              <div className="space-y-4">
                <div className="bg-white rounded-lg p-6 border-l-4 border-emerald-500">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Our Property</h3>
                  <p className="text-gray-700">
                    All content, tools, methodologies, reports, and materials provided by VAS Cyber remain our intellectual property unless explicitly transferred in writing.
                  </p>
                </div>
                <div className="bg-white rounded-lg p-6 border-l-4 border-cyan-500">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Client Data</h3>
                  <p className="text-gray-700">
                    Clients retain all rights to their data and information. We use client data solely for providing services and as permitted by our Privacy Policy.
                  </p>
                </div>
                <div className="bg-white rounded-lg p-6 border-l-4 border-gray-500">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Deliverables</h3>
                  <p className="text-gray-700">
                    Upon full payment, clients receive a license to use deliverables for their internal business purposes. Resale or redistribution is prohibited without written consent.
                  </p>
                </div>
              </div>
            </div>

            {/* Warranties and Disclaimers */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <i className="ri-alert-line text-emerald-500"></i>
                Warranties and Disclaimers
              </h2>
              <div className="bg-yellow-50 rounded-lg p-6 border-l-4 border-yellow-500 mb-4">
                <p className="text-gray-700 leading-relaxed mb-3">
                  <strong>Professional Services:</strong> We provide services using professional care and expertise consistent with industry standards. However, cybersecurity involves inherent uncertainties and evolving threats.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  <strong>No Guarantee:</strong> We cannot guarantee that our services will prevent all security incidents or that systems will be completely secure. Cybersecurity is an ongoing process requiring continuous vigilance.
                </p>
              </div>
              <div className="bg-red-50 rounded-lg p-6 border-l-4 border-red-500">
                <p className="text-gray-700 leading-relaxed">
                  <strong>Disclaimer:</strong> Except as expressly stated in service agreements, our services are provided "as is" without warranties of any kind, either express or implied, including but not limited to warranties of merchantability or fitness for a particular purpose.
                </p>
              </div>
            </div>

            {/* Limitation of Liability */}
            <div className="mb-12 bg-gradient-to-br from-gray-50 to-slate-50 rounded-2xl p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <i className="ri-shield-line text-emerald-500"></i>
                Limitation of Liability
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                To the maximum extent permitted by law:
              </p>
              <ul className="space-y-3">
                {[
                  'Our total liability for any claims arising from services shall not exceed the fees paid for those specific services',
                  'We shall not be liable for indirect, incidental, special, consequential, or punitive damages',
                  'We are not liable for damages resulting from client\'s failure to implement recommendations',
                  'We are not responsible for third-party actions or security breaches beyond our control',
                  'Liability limitations apply regardless of the form of action, whether in contract, tort, or otherwise'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3 text-gray-700">
                    <i className="ri-error-warning-line text-red-500 text-xl flex-shrink-0 mt-0.5"></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Indemnification */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <i className="ri-shield-user-line text-emerald-500"></i>
                Indemnification
              </h2>
              <p className="text-gray-700 leading-relaxed">
                You agree to indemnify, defend, and hold harmless VAS Cyber, its officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses (including legal fees) arising from your use of our services, violation of these Terms, or infringement of any third-party rights.
              </p>
            </div>

            {/* Termination */}
            <div className="mb-12 bg-gray-50 rounded-2xl p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <i className="ri-close-circle-line text-emerald-500"></i>
                Termination
              </h2>
              <div className="space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  Either party may terminate services under the following conditions:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { title: 'For Convenience', desc: 'With 30 days written notice' },
                    { title: 'For Cause', desc: 'Immediate termination for material breach' },
                    { title: 'Non-Payment', desc: 'Termination for overdue payments' },
                    { title: 'Post-Termination', desc: 'Outstanding fees remain due' }
                  ].map((term, index) => (
                    <div key={index} className="bg-white rounded-lg p-4 border border-gray-200">
                      <h3 className="text-lg font-bold text-gray-900 mb-1">{term.title}</h3>
                      <p className="text-sm text-gray-600">{term.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Governing Law */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <i className="ri-scales-3-line text-emerald-500"></i>
                Governing Law and Dispute Resolution
              </h2>
              <div className="space-y-4">
                <div className="bg-white rounded-lg p-6 border-l-4 border-emerald-500">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Governing Law</h3>
                  <p className="text-gray-700">
                    These Terms shall be governed by and construed in accordance with the laws of Ghana, without regard to its conflict of law provisions.
                  </p>
                </div>
                <div className="bg-white rounded-lg p-6 border-l-4 border-cyan-500">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Dispute Resolution</h3>
                  <p className="text-gray-700">
                    Any disputes arising from these Terms or our services shall first be attempted to be resolved through good faith negotiations. If unresolved, disputes may be submitted to arbitration or the courts of Ghana.
                  </p>
                </div>
              </div>
            </div>

            {/* Changes to Terms */}
            <div className="mb-12 bg-gradient-to-br from-emerald-50 to-cyan-50 rounded-2xl p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <i className="ri-refresh-line text-emerald-500"></i>
                Changes to Terms
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting to our website. Material changes will be communicated via email or prominent notice on our website.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Your continued use of our services after changes constitutes acceptance of the modified Terms. If you do not agree to the changes, you must discontinue use of our services.
              </p>
            </div>

            {/* Severability */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <i className="ri-file-damage-line text-emerald-500"></i>
                Severability and Waiver
              </h2>
              <div className="space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  <strong>Severability:</strong> If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary so that these Terms shall otherwise remain in full force and effect.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  <strong>Waiver:</strong> No waiver of any term of these Terms shall be deemed a further or continuing waiver of such term or any other term, and our failure to assert any right or provision under these Terms shall not constitute a waiver of such right or provision.
                </p>
              </div>
            </div>

            {/* Entire Agreement */}
            <div className="mb-12 bg-gray-50 rounded-2xl p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <i className="ri-file-check-line text-emerald-500"></i>
                Entire Agreement
              </h2>
              <p className="text-gray-700 leading-relaxed">
                These Terms, together with our Privacy Policy and any service-specific agreements, constitute the entire agreement between you and VAS Cyber regarding the use of our services and supersede all prior agreements and understandings, whether written or oral.
              </p>
            </div>

            {/* Contact */}
            <div className="bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-2xl p-8 text-white">
              <h2 className="text-3xl font-bold mb-4 flex items-center gap-3">
                <i className="ri-customer-service-2-line"></i>
                Questions About These Terms?
              </h2>
              <p className="text-lg mb-6">
                If you have any questions about these Terms of Service, please contact us:
              </p>
              <div className="space-y-3">
                <p className="flex items-center gap-3">
                  <i className="ri-building-line text-xl"></i>
                  <strong>VAS Cyber</strong>
                </p>
                <p className="flex items-center gap-3">
                  <i className="ri-map-pin-line text-xl"></i>
                  Ghana
                </p>
                <p className="flex items-center gap-3">
                  <i className="ri-mail-line text-xl"></i>
                  legal@vascyber.com
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 border-t border-gray-800 pt-8">
            <p className="text-sm text-gray-400">
              © 2025 VAS Cyber. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link
                to="/privacy"
                className="text-sm text-gray-400 hover:text-emerald-400 transition-colors duration-300 cursor-pointer"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms"
                className="text-sm text-gray-400 hover:text-emerald-400 transition-colors duration-300 cursor-pointer"
              >
                Terms of Service
              </Link>
              <a
                href="https://readdy.ai/?origin=logo"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-gray-400 hover:text-emerald-400 transition-colors duration-300 cursor-pointer"
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
